import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { AppHeader } from "../components/AppHeader";
import { LaunchStatusBanner } from "../components/LaunchStatusBanner";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { Bell, BellOff, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

function summarizeCriteria(criteria = {}) {
  const parts = [];
  if (criteria.profession && criteria.profession !== "all") parts.push(criteria.profession);
  if (Array.isArray(criteria.specialty) && criteria.specialty.length) parts.push(criteria.specialty.join(", "));
  else if (criteria.specialty) parts.push(criteria.specialty);
  if (criteria.suburbLocation?.suburb) parts.push(`${criteria.suburbLocation.suburb} (${criteria.radiusKm || 10}km)`);
  else if (criteria.suburb) parts.push(criteria.suburb);
  if (criteria.availability && criteria.availability !== "all") parts.push(`availability: ${criteria.availability}`);
  if (criteria.appointmentType && criteria.appointmentType !== "all") parts.push(criteria.appointmentType);
  if (criteria.gender && criteria.gender !== "all") parts.push(criteria.gender);
  if (criteria.language && criteria.language !== "English") parts.push(criteria.language);
  return parts.length ? parts.join(" • ") : "All clinicians";
}

export default function SavedSearches() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [searches, setSearches] = useState([]);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!user) return;
      const { data: gpRes } = await supabase.from("gp_users").select("id").eq("auth_user_id", user.id).maybeSingle();
      const gpUserId = gpRes?.id || null;
      if (!gpUserId) { if (active) setLoading(false); return; }
      const { data, error } = await supabase
        .from("gp_saved_searches")
        .select("id,name,criteria,alerts_enabled,created_at,last_alert_sent_at")
        .eq("gp_user_id", gpUserId)
        .order("created_at", { ascending: false });
      if (active) {
        if (!error) setSearches(data || []);
        setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [user]);

  const toggleAlerts = async (row) => {
    setBusyId(row.id);
    const next = !row.alerts_enabled;
    const { error } = await supabase.from("gp_saved_searches").update({ alerts_enabled: next }).eq("id", row.id);
    if (error) {
      toast.error("Could not update alert setting");
    } else {
      setSearches((prev) => prev.map((s) => (s.id === row.id ? { ...s, alerts_enabled: next } : s)));
      toast.success(next ? "Availability alerts enabled" : "Availability alerts disabled");
    }
    setBusyId(null);
  };

  const removeSearch = async (row) => {
    if (!window.confirm(`Delete saved search "${row.name}"?`)) return;
    setBusyId(row.id);
    const { error } = await supabase.from("gp_saved_searches").delete().eq("id", row.id);
    if (error) {
      toast.error("Could not delete saved search");
    } else {
      setSearches((prev) => prev.filter((s) => s.id !== row.id));
      toast.success("Saved search deleted");
    }
    setBusyId(null);
  };

  return (
    <>
      <AppHeader role="gp" />
      <LaunchStatusBanner />
      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-slate-900">Saved searches</h1>
            <p className="mt-1 text-sm text-slate-500">Save a clinician search from the directory and get notified when a matching clinician becomes available.</p>
          </div>
          <Link to="/clinicians">
            <Button variant="outline" className="gap-2"><Search className="h-4 w-4" />Find a clinician</Button>
          </Link>
        </div>

        {loading ? (
          <div className="rounded-md border border-slate-200 bg-white p-6 text-sm text-slate-500">Loading your saved searches&hellip;</div>
        ) : searches.length === 0 ? (
          <div className="rounded-md border border-dashed border-slate-300 bg-white p-8 text-center">
            <p className="text-sm text-slate-600">You haven&apos;t saved any searches yet.</p>
            <p className="mt-1 text-sm text-slate-500">Search the clinician directory, then choose <span className="font-medium">Save this search</span> to keep it here and turn on availability alerts.</p>
            <Link to="/clinicians" className="mt-4 inline-block">
              <Button className="gap-2"><Search className="h-4 w-4" />Go to directory</Button>
            </Link>
          </div>
        ) : (
          <ul className="space-y-3">
            {searches.map((row) => (
              <li key={row.id} className="rounded-md border border-slate-200 bg-white p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-heading text-base font-semibold text-slate-900">{row.name}</p>
                    <p className="mt-1 text-sm text-slate-500">{summarizeCriteria(row.criteria)}</p>
                    {row.alerts_enabled && (
                      <p className="mt-2 text-xs text-slate-400">
                        {row.last_alert_sent_at ? `Last alert sent ${new Date(row.last_alert_sent_at).toLocaleDateString()}` : "No alerts sent yet"}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <div className="flex items-center gap-2 text-sm text-slate-600" data-testid={`alert-toggle-${row.id}`}>
                      {row.alerts_enabled ? <Bell className="h-4 w-4 text-primary" /> : <BellOff className="h-4 w-4 text-slate-400" />}
                      <Switch checked={row.alerts_enabled} disabled={busyId === row.id} onCheckedChange={() => toggleAlerts(row)} />
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => removeSearch(row)} disabled={busyId === row.id} aria-label="Delete saved search">
                      <Trash2 className="h-4 w-4 text-slate-400" />
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>
    </>
  );
}
